import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
  Link as LinkIcon, TextH, Quotes, ArrowClockwise, ArrowCounterClockwise,
} from "@phosphor-icons/react";

export default function RichTextEditor({ value, onChange, placeholder = "Write your message…", testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[220px] focus:outline-none p-3",
        "data-testid": testId || "rte-editor",
      },
    },
  });

  if (!editor) return null;

  const Btn = ({ onClick, active, title, children, tid }) => (
    <button type="button" onClick={onClick} title={title} data-testid={tid}
            className={`w-8 h-8 grid place-items-center rounded ${active ? "bg-slate-200 text-slate-900" : "text-slate-500 hover:bg-slate-100"}`}>
      {children}
    </button>
  );

  const addLink = () => {
    const url = window.prompt("URL");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="flex items-center gap-0.5 border-b border-slate-200 px-1.5 py-1">
        <Btn tid="rte-bold" active={editor.isActive("bold")} title="Bold" onClick={() => editor.chain().focus().toggleBold().run()}><TextB size={14} weight="bold" /></Btn>
        <Btn tid="rte-italic" active={editor.isActive("italic")} title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()}><TextItalic size={14} /></Btn>
        <Btn tid="rte-underline" active={editor.isActive("strike")} title="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()}><TextUnderline size={14} /></Btn>
        <span className="w-px h-4 bg-slate-200 mx-1" />
        <Btn tid="rte-h2" active={editor.isActive("heading", { level: 2 })} title="Heading" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TextH size={14} /></Btn>
        <Btn tid="rte-ul" active={editor.isActive("bulletList")} title="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()}><ListBullets size={14} /></Btn>
        <Btn tid="rte-ol" active={editor.isActive("orderedList")} title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListNumbers size={14} /></Btn>
        <Btn tid="rte-quote" active={editor.isActive("blockquote")} title="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quotes size={14} /></Btn>
        <Btn tid="rte-link" active={editor.isActive("link")} title="Link" onClick={addLink}><LinkIcon size={14} /></Btn>
        <span className="w-px h-4 bg-slate-200 mx-1" />
        <Btn tid="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><ArrowCounterClockwise size={14} /></Btn>
        <Btn tid="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><ArrowClockwise size={14} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
